import { useState } from 'react';
import { Box, Collapse, Flex, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { ChevronDown, ChevronRight, Phone, Mail, MessageSquare } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { AdminNoteType } from '@duke-hq/svc-api-db/client';
import { WeeklyContactType } from '@duke-hq/svc-api/lib/enrolment/getEnrolmentWeeklyTracking';
import { getUserFullName } from '@duke-hq/lib/user';
import { djs } from '@duke-hq/djs';

type ContactItem = RouterOutputs['students']['contactHistory'][number];

function getContactIcon(noteType: string) {
  if (noteType === WeeklyContactType.OUTBOUND_CALL) {
    return Phone;
  }
  if (noteType === WeeklyContactType.OUTBOUND_EMAIL) {
    return Mail;
  }
  if (noteType === AdminNoteType.OUTBOUND_SMS || noteType === WeeklyContactType.SMS_MESSAGE) {
    return MessageSquare;
  }
  if (noteType === WeeklyContactType.SUPPORT_CHAT) {
    return MessageSquare;
  }
  return Phone;
}

function getContactTypeLabel(contact: ContactItem): string {
  return contact.label;
}

export function StudentContactsRow({ contact }: { contact: ContactItem }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const createdByName = contact.createdBy
    ? getUserFullName({
        first_name: contact.createdBy.first_name,
        last_name: contact.createdBy.last_name,
      })
    : 'Unknown';

  return (
    <Box borderBottomWidth="1px" _hover={{ bg: 'bg.subtle' }} transition="background 0.2s">
      <Flex
        p={4}
        align="center"
        justify="space-between"
        cursor="pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <HStack spacing={3} flex={1}>
          <Icon as={isExpanded ? ChevronDown : ChevronRight} boxSize={4} color="fg.muted" />

          <Icon as={getContactIcon(contact.note_type)} boxSize={5} color="fg.default" />

          <VStack align="start" spacing={0} flex={1}>
            <Text fontSize="sm" fontWeight="medium">
              {getContactTypeLabel(contact)}
            </Text>
            <Text fontSize="xs" color="fg.muted">
              {contact.createdBy ? <Trans>by {createdByName}</Trans> : <Trans>Automatic</Trans>}
            </Text>
          </VStack>
        </HStack>

        <Text fontSize="xs" color="fg.muted" minW="100px" textAlign="right">
          {djs(contact.date_time).format('D MMM YYYY')}
        </Text>
      </Flex>

      <Collapse in={isExpanded}>
        <Box px={4} pb={4} bg="bg.subtle">
          <VStack align="start" spacing={2} fontSize="sm">
            <HStack>
              <Text fontWeight="medium" color="fg.muted">
                <Trans>Date:</Trans>
              </Text>
              <Text>{djs(contact.date_time).format('D MMM YYYY h:mm A')}</Text>
            </HStack>

            {contact.createdBy ? (
              <HStack>
                <Text fontWeight="medium" color="fg.muted">
                  <Trans>Created by:</Trans>
                </Text>
                <Text>{createdByName}</Text>
              </HStack>
            ) : null}

            {contact.text !== null && contact.text !== '' && (
              <Box>
                <Text fontWeight="medium" color="fg.muted" mb={1}>
                  <Trans>Notes:</Trans>
                </Text>
                <Text whiteSpace="pre-wrap">{contact.text}</Text>
              </Box>
            )}
          </VStack>
        </Box>
      </Collapse>
    </Box>
  );
}
